import { HttpParams } from '@angular/common/http';
import { Injectable, Signal } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { Observable, switchMap } from 'rxjs';
import { BaseDatatableStore, IBaseDatatableState } from '../../../state/base-datatable-store.service';
import { ELoadStatus } from '../../../state/state.interface';
import {
  HttpUtilitiesService,
  IBaseCrudResponse,
  IGenericCrudRequestConstructionParameters,
  IGetManyResponse,
} from '../../../utilities/http-utilities.service';
import { IEditIshikawaTemplate, IIshikawaTemplate, IIshikawaTemplateRequest } from './ishikawa-templates.interface';
import { IshikawaTemplatesService } from './ishikawa-templates.service';

export interface IIshikawaTemplatesComponentState extends IBaseDatatableState<IIshikawaTemplate> {
  bulkCrudLoading: ELoadStatus;
  bulkOperationFailedData: IBaseCrudResponse[];
  data: IIshikawaTemplate[];
  dataLoading: ELoadStatus;
  dataTotal: number;
  singleCrudLoading: ELoadStatus;
}

@Injectable()
export class IshikawaTemplatesStore extends BaseDatatableStore<IIshikawaTemplate, IIshikawaTemplatesComponentState> {
  /** True while a create / update / delete request is running; the template dialog disables saving meanwhile. */
  readonly isSaving: Signal<boolean> = this.selectSignal(
    (state: IIshikawaTemplatesComponentState) => state.singleCrudLoading === ELoadStatus.loading,
  );

  readonly createOne = this.effect((trigger$: Observable<IIshikawaTemplateRequest>) =>
    trigger$.pipe(
      switchMap((request: IIshikawaTemplateRequest) => {
        this.patchState({ singleCrudLoading: ELoadStatus.loading });

        return this.service.createTemplate(request).pipe(
          tapResponse(
            () => this.patchState({ singleCrudLoading: ELoadStatus.success }),
            // The API error is shown by the interceptor; the dialog stays open with the user's input.
            () => this.patchState({ singleCrudLoading: ELoadStatus.failure }),
          ),
        );
      }),
    ),
  );

  readonly updateOne = this.effect((trigger$: Observable<IEditIshikawaTemplate>) =>
    trigger$.pipe(
      switchMap((editTemplate: IEditIshikawaTemplate) => {
        this.patchState({ singleCrudLoading: ELoadStatus.loading });

        return this.service.updateTemplate(editTemplate).pipe(
          tapResponse(
            () => this.patchState({ singleCrudLoading: ELoadStatus.success }),
            () => this.patchState({ singleCrudLoading: ELoadStatus.failure }),
          ),
        );
      }),
    ),
  );

  override readonly deleteOne = this.effect((trigger$: Observable<number>) =>
    trigger$.pipe(
      switchMap((id: number) => {
        this.patchState({ singleCrudLoading: ELoadStatus.loading });

        return this.service.deleteTemplate(id).pipe(
          tapResponse(
            () => this.patchState({ singleCrudLoading: ELoadStatus.success }),
            () => this.patchState({ singleCrudLoading: ELoadStatus.failure }),
          ),
        );
      }),
    ),
  );

  readonly loadItems = this.effect((trigger$: Observable<IGenericCrudRequestConstructionParameters>) =>
    trigger$.pipe(
      switchMap((params: IGenericCrudRequestConstructionParameters) => {
        const httpParams: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);
        this.patchState({ dataLoading: ELoadStatus.loading });

        return this.service.getTemplates(httpParams).pipe(
          tapResponse(
            (response: IGetManyResponse<IIshikawaTemplate>) =>
              this.patchState({ data: response.data, dataLoading: ELoadStatus.success, dataTotal: response.total }),
            () => this.patchState({ dataLoading: ELoadStatus.failure }),
          ),
        );
      }),
    ),
  );

  constructor(
    private readonly service: IshikawaTemplatesService,
    private readonly httpUtilities: HttpUtilitiesService,
  ) {
    super({
      bulkCrudLoading: ELoadStatus.initial,
      bulkOperationFailedData: [],
      data: [],
      dataLoading: ELoadStatus.initial,
      dataTotal: 0,
      singleCrudLoading: ELoadStatus.initial,
    });
  }
}
